/**
 * src/lib/observability.js — Structured action tracing with correlation IDs.
 *
 * Generalizes the voiceLogger.js pattern to ALL user actions.
 * Every log is fire-and-forget — never blocks UI.
 *
 * Usage:
 *   const cid = logAction('bundle.moveStage', { bundleId, from, to });
 *   // ... do work ...
 *   logResult('bundle.moveStage', 'success', { correlationId: cid, duration_ms: 42 });
 */

import { supabase } from './supabase';
import { getSessionId } from './errorReporter';
import { classifyLatency } from './observability/perfThresholds';
import { logEvent as canonicalLogEvent, setSupabaseClient as setObsSupabaseClient } from '../../lib/obs/log.mjs';

// Inject the browser supabase singleton into the spine helper at module
// load. logVoiceEvent / logAction / logResult / logFetchStart etc. all
// converge on canonicalLogEvent for the actual insert. Per PAUL Plan 92-01.
setObsSupabaseClient(supabase);

const PHASE_TAG = 'phase_a1';

// ─── Boot queue for events emitted before auth is restored (TICKET-250) ─────
// Events emitted during boot before supabase session is available would hit
// RLS 42501 (unauthenticated insert). Instead, queue them in memory until
// session restores, then flush with auth headers.
//
// ponytail: simple in-memory queue (50-cap drop-oldest). Loss on reload is OK;
// boot telemetry is observational. No persistence needed.
const EVENT_QUEUE_CAP = 50;
let _eventQueue = [];
let _sessionRestored = null; // null=boot, true=authed, false=anon
let _restoredUserId = null; // user_id from auth restore (for flush)
let _isFlushInProgress = false;

async function _tryFlushQueue() {
  if (_isFlushInProgress || _eventQueue.length === 0 || _sessionRestored !== true) {
    return;
  }
  _isFlushInProgress = true;
  const toFlush = _eventQueue.splice(0, _eventQueue.length);
  try {
    for (const item of toFlush) {
      try {
        // Refill user_id from restored session (boot-queued items had null)
        item.user_id = _restoredUserId;
        await canonicalLogEvent(item, { mode: 'lenient' });
      } catch {
        // Silent catch — individual event failures don't block the queue
      }
    }
  } finally {
    _isFlushInProgress = false;
  }
}

function _queueOrEmit(item) {
  // Boot phase: queue until session is known
  if (_sessionRestored === null) {
    if (_eventQueue.length >= EVENT_QUEUE_CAP) {
      _eventQueue.shift(); // Drop oldest on overflow
    }
    _eventQueue.push(item);
    return;
  }

  // Anon phase: silent drop (no fetch, no console.error)
  if (_sessionRestored === false) {
    return;
  }

  // Authed phase: emit directly
  canonicalLogEvent(item, { mode: 'lenient' }).catch(() => {});
}

function _markSessionState(session) {
  // STAB-1 (night-watch 2026-07-10): DON'T latch on the first non-null mark.
  // The old `if (_sessionRestored !== null) return` pinned the state forever, so
  // a logged-out load (getSession → null → anon) that then signs in WITHOUT a
  // page reload — the normal in-place login, and every expired-session re-login —
  // was ignored: onAuthStateChange fired SIGNED_IN but this returned early, the
  // layer stayed 'anon', and _queueOrEmit silently DROPPED all client telemetry
  // until a hard refresh. Instead: 'authed' is terminal (stays authed), but an
  // 'anon' state is promoted to 'authed' on a later sign-in (flushing anything
  // still queued from boot).
  if (_sessionRestored === true) return; // authed is terminal — never demote/rethrash
  const authed = !!session?.user;
  if (_sessionRestored === false && !authed) return; // still anon — nothing changed
  _sessionRestored = authed;
  if (authed) {
    _restoredUserId = session.user.id || null;
    _tryFlushQueue(); // Flush queued events now that auth is ready
  }
}

// Detect session restore on auth state change. The first onAuthStateChange
// callback after boot tells us whether auth succeeded (user exists) or failed
// (anon). Then we know whether to flush the queue or drop it silently.
supabase.auth?.onAuthStateChange?.((event, session) => {
  _markSessionState(session);
});

// Resolve the state PROACTIVELY too: getSession() answers from storage without
// waiting for an auth event (public pages resolve null → anon). Environments
// with no auth surface at all (unit tests, legacy mocks) keep the pre-queue
// direct-emit behavior — queuing forever there broke 8 existing suites.
if (typeof supabase.auth?.getSession === 'function') {
  supabase.auth.getSession()
    .then((r) => _markSessionState(r?.data?.session || null))
    .catch(() => { if (_sessionRestored === null) { _sessionRestored = true; _tryFlushQueue(); } });
} else if (!supabase.auth?.onAuthStateChange) {
  _sessionRestored = true;
}

// Export for testing: force queue state and session detection for integration tests
export function _setBootQueueState(state) {
  _sessionRestored = state;
  _restoredUserId = null;
  _eventQueue = []; // Reset queue on state change
  if (state === true) _tryFlushQueue();
}

// Export for testing: manually trigger session state marking
export function _markSessionStateForTest(session) {
  _sessionRestored = null; // Reset to allow re-marking
  _markSessionState(session);
}

// Export for testing (STAB-1): simulate a LATER onAuthStateChange without resetting
// the latched state — the real in-place-login path (supabase.auth.onAuthStateChange
// → _markSessionState). Lets a test drive the anon→authed promotion.
export function _onAuthEventForTest(session) {
  _markSessionState(session);
}

// Export for testing: read queue state
export function _getBootQueueLength() {
  return _eventQueue.length;
}

// Export for testing: read session state
export function _getBootQueueSessionState() {
  return _sessionRestored;
}

export function createCorrelationId() {
  return crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Ambient trace context (Phase A1 — D) ─────────────────────────────
// Callers set { activeTab, contextProjectId, contextBundleId, contextDealId, userRole }
// via setTraceContext(). Every _log call automatically merges it in.
let _traceContext = {};
export function setTraceContext(patch = {}) {
  _traceContext = { ..._traceContext, ...patch };
}
export function getTraceContext() { return { ..._traceContext }; }
export function clearTraceContext() { _traceContext = {}; }

// ─── Active nav correlation (Phase A1 — B) ────────────────────────────
// Set by logRouteChange on start; read by fetch_trace so queries can pivot
// "all fetches during nav X".
let _activeNavCorrelationId = null;
export function getActiveNavCorrelationId() { return _activeNavCorrelationId; }

// ─── Ceremony sampling (TICKET-325) ───────────────────────────────────
//
// The spine is not free: it lives in `app_error_logs` on a FREE-plan Supabase
// (512 MB). On 2026-07-22 that table was 143 MB / 113,866 rows — 28% of a DB
// sitting at 502 MB (98% of quota), and ~89% of those rows were happy-path
// ceremony: fetch_trace start/succeeded pairs and action_intent/action_result
// pairs. Retention was working fine (oldest row exactly 14d); the volume was
// the bug. This is the SAME class as the voice-drain flood (OPS-1, 28k empty
// rows/21d) — the fix there was "emit on signal, sample the no-op", and this
// is that fix applied at the client choke point every emitter routes through.
//
// Contract: signal is NEVER sampled. Failures, non-2xx, and slow calls always
// emit at 100%. Only the boring success path is thinned.
const CEREMONY_TYPES = new Set(['action_intent', 'action_result', 'fetch_trace']);

// Keep 1 in N ceremony traces. Calibration knob, not a constant: real traffic
// volume drifts and the quota headroom it buys is what matters — re-tune from
// the row census (`select error_type, count(*) from app_error_logs group by 1`)
// rather than guessing. 8 → ~95k rows/14d becomes ~12k.
let _ceremonySampleRate = 8;

/** Test seam + tuning knob. Rate 1 = keep everything (no sampling). */
export function setCeremonySampleRate(n) {
  _ceremonySampleRate = Math.max(1, Number(n) || 1);
}
export function getCeremonySampleRate() { return _ceremonySampleRate; }

/**
 * Is this row signal (always keep) rather than ceremony (sample)?
 * Anything that could explain a failure or a slowdown is signal.
 */
export function isSignalEvent(errorType, metadata) {
  if (!CEREMONY_TYPES.has(errorType)) return true; // errors, warnings, everything else
  const m = metadata || {};
  // Explicit failure markers (logFetchFailure sets error_category + error_message).
  if (m.error_category || m.error_message || m.error_code) return true;
  // Non-2xx HTTP status — the 400/401 rows are exactly what debugging needs.
  const status = Number(m.status);
  if (Number.isFinite(status) && (status < 200 || status >= 300)) return true;
  // logResult outcomes other than success (`not_found`, `forbidden`, `error`, ...).
  if (m.outcome != null && !/^(success|ok|200)$/i.test(String(m.outcome))) return true;
  // Slow calls stay at 100% — a perf regression must never be sampled away.
  if (m.classification === 'warning' || m.classification === 'critical') return true;
  return false;
}

/**
 * Deterministic 1-in-N gate keyed on correlationId.
 *
 * Hashing the correlationId (rather than Math.random per row) keeps an
 * intent/result — or a fetch start/succeeded — PAIR together: both survive or
 * both drop. Half a pair is worse than no pair, because a lone `action_intent`
 * reads as "the action never completed".
 */
export function passesCeremonySample(metadata) {
  if (_ceremonySampleRate <= 1) return true;
  const cid = metadata?.correlationId;
  if (!cid) return true; // unpairable — keep it rather than guess
  const s = String(cid);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return (h >>> 0) % _ceremonySampleRate === 0;
}

// ─── Core write (fire-and-forget) ─────────────────────────────────────
//
// 2026-04-20 — defensive against missing supabase.auth in test env. The
// previous `supabase.auth.getUser().then(...)` threw synchronously when a
// test mocked `supabase` but not `supabase.auth`, corrupting the React
// render path from any useEffect that calls logAction/logResult. The
// whole logger is fire-and-forget; any thrown/rejected path here must
// never surface to the caller.
//
// TICKET-250: During boot (before auth restore), events are queued in memory.
// Once auth settles (authed or anon), queued events are flushed (authed only) or
// silently dropped (anon). Post-boot events go direct → spine (authed) or silent
// drop (anon).
function _log(errorType, message, metadata) {
  try {
    // If we're in anon phase, drop silently (no auth lookup needed)
    if (_sessionRestored === false) {
      return;
    }

    // TICKET-325 — thin the happy-path ceremony before it costs a row.
    // Signal (failures / non-2xx / slow) is never sampled; see isSignalEvent.
    if (!isSignalEvent(errorType, metadata) && !passesCeremonySample(metadata)) {
      return;
    }

    // Optimization: if session is already restored and we have the user_id,
    // use it directly instead of calling getUser() again
    if (_sessionRestored === true && _restoredUserId !== null) {
      try {
        const fullMeta = { ..._traceContext, ...metadata, sessionId: getSessionId() };
        const item = {
          user_id: _restoredUserId,
          error_type: errorType,
          message: (message || '').slice(0, 2000),
          metadata: fullMeta,
          app_version: typeof import.meta !== 'undefined' && import.meta.env
                         ? (import.meta.env.VITE_APP_VERSION || null)
                         : null,
          trace_id: metadata?.trace_id ?? null,
          session_id: metadata?.session_id ?? getSessionId() ?? null,
          source: metadata?.source ?? 'client',
          page: metadata?.page ?? (typeof window !== 'undefined' ? window.location.pathname : null),
          scope_type: metadata?.scope_type ?? null,
          scope_id: metadata?.scope_id ?? null,
          module: metadata?.module ?? null,
          step: metadata?.step ?? null,
          status: metadata?.status ?? null,
          duration_ms: metadata?.duration_ms ?? metadata?.latency_ms ?? null,
          error_code: metadata?.error_code ?? null,
        };
        canonicalLogEvent(item, { mode: 'lenient' }).catch(() => {});
        return;
      } catch { /* never throw from logger */ }
    }

    const authApi = supabase?.auth;
    const p = authApi?.getUser ? authApi.getUser() : Promise.resolve({ data: null });
    Promise.resolve(p).then(({ data } = {}) => {
      try {
        const fullMeta = { ..._traceContext, ...metadata, sessionId: getSessionId() };
        // Canonical fields (PAUL Plan 92-01) — pass through to logEvent if
        // caller supplied them in metadata. Legacy callers don't pass any of
        // these, so they all become null and the row shape stays identical
        // to pre-migration. New callers (voice trace_id) opt in by passing
        // the canonical fields in metadata.
        const item = {
          user_id: data?.user?.id || null,
          error_type: errorType,
          message: (message || '').slice(0, 2000),
          metadata: fullMeta,                // legacy passthrough — no canonical wrap
          app_version: typeof import.meta !== 'undefined' && import.meta.env
                         ? (import.meta.env.VITE_APP_VERSION || null)
                         : null,
          trace_id: metadata?.trace_id ?? null,
          session_id: metadata?.session_id ?? getSessionId() ?? null,
          source: metadata?.source ?? 'client',
          page: metadata?.page ?? (typeof window !== 'undefined' ? window.location.pathname : null),
          scope_type: metadata?.scope_type ?? null,
          scope_id: metadata?.scope_id ?? null,
          module: metadata?.module ?? null,  // legacy callers don't tag module — leave null
          step: metadata?.step ?? null,
          status: metadata?.status ?? null,
          duration_ms: metadata?.duration_ms ?? metadata?.latency_ms ?? null,
          error_code: metadata?.error_code ?? null,
        };
        // Route through queue or direct emit based on boot phase
        _queueOrEmit(item);
      } catch { /* never throw from logger */ }
    }).catch(() => {});
  } catch { /* never throw from logger */ }
}

// ─── Public API ───────────────────────────────────────────────────────

/** Log intent before an action. Returns correlationId. */
export function logAction(action, metadata = {}) {
  const correlationId = createCorrelationId();
  _log('action_intent', action, { ...metadata, correlationId, action, timestamp: Date.now() });
  return correlationId;
}

/** Log result after an action completes. */
export function logResult(action, outcome, metadata = {}) {
  _log('action_result', `${action}: ${outcome}`, { ...metadata, action, outcome, timestamp: Date.now() });
}

/** Log a reload event with its trigger. */
export function logReload(trigger, metadata = {}) {
  _log('reload_event', `reload triggered by ${trigger}`, { ...metadata, trigger, timestamp: Date.now() });
}

/** Log an optimistic merge decision (only on TTL expiry — not happy path). */
export function logMergeDecision(entityId, decision, metadata = {}) {
  _log('merge_decision', `${entityId}: ${decision}`, { ...metadata, entityId, decision, timestamp: Date.now() });
}

/**
 * Generic typed event emit. Used by Phase B1 (render_exclusion, entity_stage_drift)
 * and future phases (A2 render_audit, ui_runtime_error, Z cross_view_consistency_check).
 * Callers MUST pass phase_tag in metadata so bugs can be isolated post-hoc via app_error_logs.
 */
export function logEvent(errorType, message, metadata = {}) {
  _log(errorType, message, metadata);
}

/**
 * Navigation lifecycle emitter (Phase A1).
 * Emits `route_change_started` immediately; returns a handle with `complete()` + `fail()`.
 * latency_ms is measured via performance.now() between start and complete/fail.
 */
export function logRouteChange(routeFrom, routeTo, ctx = {}) {
  const correlationId = createCorrelationId();
  _activeNavCorrelationId = correlationId;
  const start = performance.now();
  _log('nav_trace', 'route_change_started', {
    phase_tag: PHASE_TAG,
    correlationId,
    route_from: routeFrom,
    route_to: routeTo,
    ...ctx,
  });
  return {
    correlationId,
    complete(extra = {}) {
      const latency_ms = Math.round(performance.now() - start);
      _log('nav_trace', 'route_change_completed', {
        phase_tag: PHASE_TAG,
        correlationId,
        route_from: routeFrom,
        route_to: routeTo,
        latency_ms,
        classification: classifyLatency(latency_ms, 'route'),
        ...ctx,
        ...extra,
      });
    },
    fail(extra = {}) {
      const latency_ms = Math.round(performance.now() - start);
      _log('nav_trace', 'route_change_failed', {
        phase_tag: PHASE_TAG,
        correlationId,
        route_from: routeFrom,
        route_to: routeTo,
        latency_ms,
        ...ctx,
        ...extra,
      });
    },
  };
}

/**
 * Categorize a fetch error for SQL pivots. (Phase A1 — C)
 * network / timeout / http_4xx / http_5xx / abort / parse_error / unknown
 */
export function categorizeFetchError({ status, errName, errMessage } = {}) {
  if (errName === 'AbortError') return 'abort';
  if (status >= 500 && status < 600) return 'http_5xx';
  if (status >= 400 && status < 500) return 'http_4xx';
  const msg = String(errMessage || '').toLowerCase();
  if (msg.includes('timeout') || msg.includes('timed out')) return 'timeout';
  if (msg.includes('failed to fetch') || msg.includes('network')) return 'network';
  if (msg.includes('json') || msg.includes('parse') || msg.includes('unexpected token')) return 'parse_error';
  return 'unknown';
}

/**
 * Fetch lifecycle emitters (Phase A1).
 * Called from apiFetch — additive to existing logAction/logResult.
 */
export function logFetchStart({ correlationId, action, url, method }) {
  _log('fetch_trace', 'data_fetch_started', {
    phase_tag: PHASE_TAG,
    correlationId,
    nav_correlationId: _activeNavCorrelationId,
    action,
    url: (url || '').slice(0, 200),
    method,
  });
}

export function logFetchSuccess({ correlationId, action, latency_ms, record_count, content_length, status }) {
  _log('fetch_trace', 'data_fetch_succeeded', {
    phase_tag: PHASE_TAG,
    correlationId,
    nav_correlationId: _activeNavCorrelationId,
    action,
    latency_ms,
    classification: classifyLatency(latency_ms, 'query'),
    record_count,
    content_length,
    status,
  });
}

export function logFetchFailure({ correlationId, action, latency_ms, status, error, retry_count }) {
  const error_category = categorizeFetchError({
    status,
    errName: error?.name,
    errMessage: error?.message || error,
  });
  _log('fetch_trace', 'data_fetch_failed', {
    phase_tag: PHASE_TAG,
    correlationId,
    nav_correlationId: _activeNavCorrelationId,
    action,
    latency_ms,
    status: status || null,
    error_category,
    error_message: String(error?.message || error || 'unknown').slice(0, 200),
    retry_count: retry_count || 0,
  });
}
